// --------------------------------------------------------------
// Created On: 2024-03-28
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-12
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Error404Page from "../Error404Page/Error404Page";
import Spinner from "../../components/Spinner/Spinner";
import LogoLink from "./LogoLink/LogoLink";
import PumpInfo from "./PumpInfo/PumpInfo";
import PumpLinks from "./PumpLinks/PumpLinks";
import AdditionalManuals from "./AdditionalManuals/AdditionalManuals";
import useApi from "../../hooks/useApi";
import { ENGINE_CONTROLLER_MANUALS, ENGINE_MANUALS } from "../../constants/manuals";
import { RPM_SCAN_ASSET_URL, SHOP_URL } from "../../constants/urls";
import { API, SHOW_ADDITIONAL_MANUALS } from "../../constants/miscellaneous";

// Scan QR Code Page that shows a pump's information when its QR code is scanned.
export default function ScanQrCodePage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [serial, setSerial] = useState<string>("");
  const [copilotId, setCopilotId] = useState<number | null>(null);
  const [bomNumber, setBomNumber] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [pumpType, setPumpType] = useState<string>("");
  const [impellerTrim, setImpellerTrim] = useState<number | null>(null);
  const [impellerTrimAngle, setImpellerTrimAngle] = useState<number | null>(null);
  const [estimatedShipDate, setEstimatedShipDate] = useState<string | null>(null);
  const [manual1Name, setManual1Name] = useState<string>("");
  const [manual2Name, setManual2Name] = useState<string | null>(null);
  const [explodedDrawingNumber, setExplodedDrawingNumber] = useState<string | null>(null);
  const { serialNumber } = useParams();

  // Get pump data from API using the serial number.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/serial/${serialNumber}`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setSerial(responseBody.serialNumber.toString());
        setCopilotId(responseBody.copilotId);
        setBomNumber(responseBody.bomNumber);
        setDescription(responseBody.description);
        setPumpType(responseBody.pumpType);
        setImpellerTrim(responseBody.impellerTrim);
        setImpellerTrimAngle(responseBody.impellerTrimAngle);
        setEstimatedShipDate(responseBody.estimatedShipDate);
        setManual1Name(responseBody.manual1Name);
        setManual2Name(responseBody.manual2Name);
        setExplodedDrawingNumber(responseBody.explodedDrawingNumber);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  return failedToLoad ? (
    <Error404Page />
  ) : (
    <div className="container mt-5">
      <Spinner loading={loading} />
      {/* Row containing the page's content in one column and sidebar LogoLinks in the other. */}
      <div className="row gx-5">
        {/* Column containing the page's content (i.e. PumpInfo, PumpLinks, & AdditionalManuals). */}
        <div className="col-12 col-md-6 pl-5 text-center mb-3">
          <PumpInfo
            serialNumber={serial}
            copilotId={copilotId}
            bomNumber={bomNumber}
            description={description}
            pumpType={pumpType}
            impellerTrim={impellerTrim}
            impellerTrimAngle={impellerTrimAngle}
            estimatedShipDate={estimatedShipDate}
          />
        </div>
        <div className="col-12 col-md-4 mb-3">
          <PumpLinks
            loading={loading}
            manual1Name={manual1Name}
            manual2Name={manual2Name}
            serialNumber={serial}
            bomNumber={bomNumber}
            pumpType={pumpType}
            explodedDrawingNumber={explodedDrawingNumber}
          />
        </div>
        <div className="col-12 col-md-2 text-center mb-3 align-self-center">
          <div className="row align-items-center h-100">
            <div className="col-6 col-lg-12">
              <LogoLink logoUrl="/cornell_logo.png" buttonUrl={SHOP_URL} buttonDescription="Order Parts Online" />
            </div>
            <div className="col-6 col-lg-12 ">
              <LogoLink
                logoUrl="/rpm_squared_red_logo.png"
                buttonUrl={`${RPM_SCAN_ASSET_URL}${bomNumber}/${serialNumber}`}
                buttonDescription="View Asset in RPM²"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row gx-5">
        <div className="col-12">
          {SHOW_ADDITIONAL_MANUALS && ENGINE_MANUALS.length > 0 && ENGINE_CONTROLLER_MANUALS.length > 0 && (
            <AdditionalManuals />
          )}
        </div>
      </div>
    </div>
  );
}

interface ResponseBody {
  serialNumber: string;
  copilotId: number | null;
  bomNumber: string;
  description: string;
  pumpType: string;
  impellerTrim: number | null;
  impellerTrimAngle: number | null;
  estimatedShipDate: string | null;
  manual1Name: string;
  manual2Name: string | null;
  explodedDrawingNumber: string | null;
}
